"use client";

import { useGetCourses } from "@/services/courses/getCourses";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import Text from "../Text";
import { Swiper, SwiperSlide } from "swiper/react";
import SkeletonCourses from "../FilteredCourses/SkeletonCourses";
import GradientLightText from "../GradientLightText";
import { useGetCourseSkills } from "@/services/courses/getCourseSkills";
import { SkillAreaType } from "@/types/CourseSkill";
import { CourseTypeType } from "@/types/Courses";

const LazyFilteredCourse = dynamic(() => import("../FilteredCourses"), {
  ssr: false,
});

type FilterType = {
  label: string;
  value: string;
};

type FindCoursesProps = {
  component: {
    type: string;
    isLight?: boolean;
    group?: string;
    isSecondLevelFilterForbiden?: boolean;
    isFiltersHidden?: boolean;
    slug?: string;
  };
};

const FindCourses = ({ component }: FindCoursesProps) => {
  const compareCourseSkills = (a: SkillAreaType, b: SkillAreaType): number => {
    return a.name?.localeCompare(b.name!, "ru")!;
  };

  const compareCourse = (a: FilterType, b: FilterType): number => {
    return a.label?.localeCompare(b.label!, "ru")!;
  };

  const t = useTranslations("common");
  const { data: skills } = useGetCourseSkills({});
  const [selectedGroup, setSelectedGroup] = useState<string>(
    component.group ? "" : t.raw("exploreTheAcademy.filters")[0].value
  );
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>("");
  const [selectedPath, setSelectedPath] = useState<string>(
    component.group || ""
  );
  const [selectedSkill, setSelectedSkill] = useState<SkillAreaType | undefined>(
    skills
      ?.sort(compareCourseSkills)
      .sort((a, b) => (a.name === "ЗДОРОВЬЕ" ? -1 : 1))[0]
  );
  const [selectedSkillLevelOne, setSelectedSkillLevelOne] = useState<
    number | undefined
  >();
  const [selectedSkillLevelTwo, setSelectedSkillLevelTwo] = useState<
    number | undefined
  >();

  const {
    data: courses,
    isValidating,
    isLoading,
  } = useGetCourses({
    path: selectedPath,
    discipline: selectedDiscipline,
    skill_area: selectedSkillLevelTwo
      ? selectedSkillLevelTwo
      : selectedSkillLevelOne
      ? selectedSkillLevelOne
      : undefined,
  });

  const onlyCourses = courses?.filter(
    (course) =>
      !course.options?.Quarter &&
      course.title !== "ПРОДУКТ: ДОПЛАТА ЗА КУРС" &&
      course.title !== "Путь к Себе (Фестиваль на Бали)" &&
      course.title !== "ФЕСТИВАЛЬ: ПОВЕЛИТЕЛИ СТИХИЙ" &&
      course.type !== CourseTypeType.Mini
  );

  const resetSelectedFields = () => {
    setSelectedDiscipline("");
    setSelectedPath("");
    setSelectedSkill({});
    setSelectedSkillLevelOne(undefined);
    setSelectedSkillLevelTwo(undefined);
  };

  useEffect(() => {
    const levelOneSkills = skills
      ?.sort(compareCourseSkills)
      .sort((a, b) => (a.name === "ЗДОРОВЬЕ" ? -1 : 1))
      .filter((skill) => skill.level === 1);
    setSelectedSkillLevelOne(
      component.group ? undefined : levelOneSkills?.[0].id
    );
    setSelectedSkillLevelTwo(
      component.group || component.isSecondLevelFilterForbiden
        ? undefined
        : levelOneSkills?.[0].has_children
        ? levelOneSkills?.[0].child_skill_areas?.sort(compareCourseSkills)[0].id
        : undefined
    );
  }, [skills, component]);

  const renderSecondRowFilter = (value: string) => {
    switch (value) {
      case "disciplines":
        return (
          <div className="find-courses__filters-holder">
            <Text element="p" elementStyle="h2" fontFamily="noto-serif">
              {t("disciplines.title")}
            </Text>
            <ul className="find-courses__filters">
              {t
                .raw("disciplines.filters")
                .sort(compareCourse)
                .map((filter: FilterType) => (
                  <li
                    key={filter.value}
                    className={`find-courses__filters-item ${
                      selectedDiscipline === filter.value
                        ? "find-courses__filters-item--active"
                        : ""
                    }`.trim()}
                  >
                    <button
                      disabled={selectedDiscipline === filter.value}
                      onClick={() => setSelectedDiscipline(filter.value)}
                      className="weight-700"
                    >
                      {filter.label}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        );
      case "paths":
        return (
          <div className="find-courses__filters-holder">
            <Text element="p" elementStyle="h2" fontFamily="noto-serif">
              {t("paths.title")}
            </Text>
            <ul className="find-courses__filters">
              {t
                .raw("paths.filters")
                .sort(compareCourse)
                .map((filter: FilterType) => (
                  <li
                    key={filter.value}
                    className={`find-courses__filters-item ${
                      selectedPath === filter.value
                        ? "find-courses__filters-item--active"
                        : ""
                    }`.trim()}
                  >
                    <button
                      disabled={selectedPath === filter.value}
                      onClick={() => setSelectedPath(filter.value)}
                      className="weight-700"
                    >
                      {filter.label}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        );
      case "skills":
        return (
          <>
            <div className="find-courses__filters-holder">
              <Text element="p" elementStyle="h2" fontFamily="noto-serif">
                {t("skills.title")}
              </Text>
              <ul className="find-courses__filters">
                {skills
                  ?.sort(compareCourseSkills)
                  .sort((a, b) => (a.name === "ЗДОРОВЬЕ" ? -1 : 1))
                  .filter((skill) => skill.level === 1)
                  .map((filter: SkillAreaType) => (
                    <li
                      key={filter.id}
                      className={`find-courses__filters-item ${
                        selectedSkillLevelOne === filter.id
                          ? "find-courses__filters-item--active"
                          : ""
                      }`.trim()}
                    >
                      <button
                        disabled={selectedSkillLevelOne === filter.id}
                        onClick={() => {
                          setSelectedSkill(filter);
                          setSelectedSkillLevelOne(filter.id);

                          if (!component.isSecondLevelFilterForbiden) {
                            setSelectedSkillLevelTwo(
                              filter.has_children
                                ? filter.child_skill_areas
                                    ?.sort(compareCourseSkills)
                                    .sort((a, b) =>
                                      a.name === "ЗДОРОВЬЕ" ? -1 : 1
                                    )[0].id
                                : undefined
                            );
                          } else {
                            setSelectedSkillLevelTwo(undefined);
                          }
                        }}
                        className="weight-700"
                      >
                        {filter.name}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
            {selectedSkill?.has_children &&
            !component.isSecondLevelFilterForbiden ? (
              <div className="find-courses__filters-holder">
                <Text element="p" elementStyle="h2" fontFamily="noto-serif">
                  <GradientLightText element="span">
                    {selectedSkill.name}
                  </GradientLightText>
                </Text>
                <ul className="find-courses__filters">
                  {selectedSkill.child_skill_areas
                    ?.sort(compareCourseSkills)
                    .sort((a, b) => (a.name === "ЗДОРОВЬЕ" ? -1 : 1))
                    .map((filter: any) => (
                      <li
                        key={filter.id}
                        className={`find-courses__filters-item ${
                          selectedSkillLevelTwo === filter.id
                            ? "find-courses__filters-item--active"
                            : ""
                        }`.trim()}
                      >
                        <button
                          disabled={selectedSkillLevelTwo === filter.id}
                          onClick={() => {
                            setSelectedSkillLevelTwo(filter.id);
                          }}
                          className="weight-700"
                        >
                          {filter.name}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </>
        );
    }
  };

  return (
    <div
      className={"find-courses"
        .concat(component.group ? " find-courses--path" : "")
        .concat(component.isLight ? " find-courses--light" : "")
        .trim()}
    >
      {!component.isFiltersHidden ? (
        <>
          <div className="find-courses__filters-holder">
            <Text element="p" elementStyle="h2" fontFamily="noto-serif">
              {t("exploreTheAcademy.title")}
            </Text>
            <ul className="find-courses__filters">
              {t.raw("exploreTheAcademy.filters").map((filter: FilterType) => (
                <li
                  key={filter.value}
                  className={`find-courses__filters-item ${
                    selectedGroup === filter.value
                      ? "find-courses__filters-item--active"
                      : ""
                  }`.trim()}
                >
                  <button
                    disabled={selectedGroup === filter.value}
                    onClick={() => {
                      resetSelectedFields();
                      setSelectedGroup(filter.value);

                      switch (filter.value) {
                        case "paths":
                          setSelectedPath(t.raw("paths.filters")[0].value);
                          break;
                        case "disciplines":
                          setSelectedDiscipline(
                            t.raw("disciplines.filters")[0].value
                          );
                          break;
                        case "skills":
                          const levelOneSkills = skills
                            ?.sort(compareCourseSkills)
                            .sort((a, b) => (a.name === "ЗДОРОВЬЕ" ? -1 : 1))
                            .filter((skill) => skill.level === 1);
                          setSelectedSkill(levelOneSkills?.[0]);
                          setSelectedSkillLevelOne(levelOneSkills?.[0].id);
                          setSelectedSkillLevelTwo(
                            component.isSecondLevelFilterForbiden
                              ? undefined
                              : levelOneSkills?.[0]?.child_skill_areas
                                  ?.sort(compareCourseSkills)
                                  .sort((a, b) =>
                                    a.name === "ЗДОРОВЬЕ" ? -1 : 1
                                  )[0].id
                          );
                      }
                    }}
                    className="weight-700"
                  >
                    {filter.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {renderSecondRowFilter(selectedGroup)}
        </>
      ) : null}
      {isLoading || isValidating ? (
        <Swiper
          className="find-courses__list"
          slidesPerView={1.2}
          spaceBetween={10}
          breakpoints={{
            768: {
              slidesPerView: 2,
            },
            1200: {
              slidesPerView: 4,
            },
          }}
        >
          {[0, 1, 2, 3].map((a) => (
            <SwiperSlide key={a}>
              <SkeletonCourses />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <>
          {onlyCourses && onlyCourses.length > 0 ? (
            <LazyFilteredCourse courses={onlyCourses!} slug={component.slug} />
          ) : (
            <div className="find-courses__empty">
              <Text
                element="p"
                elementStyle="h2"
                fontFamily="noto-serif"
                className="text-center"
              >
                {t("noAvailableCourses")}
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FindCourses;
